import { forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from './badge';

/**
 * Campaign status pill. Translates the status label via i18n keys
 * (`campaign_status_<value>`) and applies a colored leading dot. For
 * `running` and `queued` (active in-flight states) the dot pulses with
 * the Volt halo so a glance at the table shows what's "live" right now.
 */
export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: CampaignStatus;
  /** Override the translated label (rarely needed). */
  label?: string;
}

const statusToBadgeVariant = {
  draft: 'gray',
  scheduled: 'info',
  queued: 'info',
  running: 'volt',
  paused: 'warn',
  completed: 'success',
  failed: 'danger',
  cancelled: 'danger',
} as const;

// Translation keys live in es.json/en.json under campaign_status_<value>;
// see frontend/src/i18n.
const statusToI18nKey: Record<CampaignStatus, string> = {
  draft: 'campaign_status_draft',
  scheduled: 'campaign_status_scheduled',
  queued: 'campaign_status_queued',
  running: 'campaign_status_running',
  paused: 'campaign_status_paused',
  completed: 'campaign_status_completed',
  failed: 'campaign_status_failed',
  cancelled: 'campaign_status_cancelled',
};

const dotClass: Record<CampaignStatus, string> = {
  draft: 'bg-text-4',
  scheduled: 'bg-info',
  queued: 'bg-volt animate-volt-pulse',
  running: 'bg-volt animate-volt-pulse',
  paused: 'bg-warn',
  completed: 'bg-success',
  failed: 'bg-danger',
  cancelled: 'bg-danger',
};

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ status, label, className, ...props }, ref) => {
    const { t } = useTranslation();
    const variant = statusToBadgeVariant[status];
    const text = label ?? t(statusToI18nKey[status]);
    return (
      <Badge ref={ref} variant={variant} className={cn('gap-1.5', className)} {...props}>
        <span
          aria-hidden="true"
          className={cn('w-2 h-2 rounded-full', dotClass[status])}
        />
        {text}
      </Badge>
    );
  },
);
StatusPill.displayName = 'StatusPill';
